import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft, Rss, Shield, Newspaper, ExternalLink,
  RefreshCw, ChevronRight, BookOpen,
} from "lucide-react";
import { SiteHeader, SiteFooter } from "../components/dnh/SiteChrome";
import { HighlightCard } from "../components/dnh/highlights/HighlightCard";
import { HighlightBriefPanel } from "../components/dnh/highlights/HighlightBriefPanel";
import { getArchive, getSourceDetail, getCategories, getSourcesByCountry } from "../utils/api";
import { useAuth } from "../components/dnh/AuthContext";

/* ============================================================
   DNH — /sources/:source_id
   Publisher-specific highlights page with SEO-friendly URL.
   Breadcrumb: Sources → [Publication Name]
   ============================================================ */

const BASE_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 24;

// ── Source type badge ─────────────────────────────────────────
const TYPE_CFG = {
  official:    { label: "Official / Auth", icon: <Shield size={11} />,   cls: "bg-amber-50 text-amber-700 border-amber-200" },
  publication: { label: "Publication",     icon: <Newspaper size={11} />, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

const TypeBadge = ({ type }) => {
  const cfg = TYPE_CFG[type] || TYPE_CFG.publication;
  return (
    <span className={`inline-flex items-center gap-1 h-5 px-2.5 rounded-[4px] border font-mono text-[9.5px] uppercase tracking-[0.06em] ${cfg.cls}`}>
      {cfg.icon}{cfg.label}
    </span>
  );
};

const RssBadge = () => (
  <span className="inline-flex items-center gap-1 h-5 px-2.5 rounded-[4px] border bg-blue-50 text-blue-700 border-blue-200 font-mono text-[9.5px] uppercase tracking-[0.06em]">
    <Rss size={10} />RSS
  </span>
);

// ── Category filter pill ──────────────────────────────────────
const CatPill = ({ label, active, onClick }) => (
  <button
    onClick={onClick}
    className={`h-7 px-3.5 rounded-full border font-mono text-[10px] uppercase tracking-[0.08em] transition-colors ${
      active
        ? "border-[var(--ink)] bg-[var(--ink)] text-[var(--paper)]"
        : "border-[var(--hairline)] text-[var(--muted)] hover:border-[var(--ink)] hover:text-[var(--ink)]"
    }`}
  >
    {label}
  </button>
);

// ══════════════════════════════════════════════════════════════
// SourceDetail page
// ══════════════════════════════════════════════════════════════
export default function SourceDetail() {
  const { sourceId } = useParams();
  const { user } = useAuth();

  const [source, setSource]       = useState(null);
  const [srcError, setSrcError]   = useState(false);
  const [stories, setStories]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage]           = useState(1);
  const [hasMore, setHasMore]     = useState(false);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("");
  const [activeStory, setActiveStory] = useState(null);
  const [error, setError]         = useState(false);
  const [related, setRelated]     = useState([]);
  const snapshot = useRef(null);
  const reqVer   = useRef(0);

  // Load source metadata + categories in parallel
  useEffect(() => {
    getSourceDetail(sourceId)
      .then(s => {
        setSource(s);
        // Once we have country, fetch related sources
        if (s?.country_slug) {
          getSourcesByCountry(s.country_slug)
            .then(d => setRelated(
              (d?.sources || []).filter(r => r.source_id !== sourceId).slice(0, 8)
            ))
            .catch(() => {});
        }
      })
      .catch(() => setSrcError(true));
    getCategories()
      .then(d => setCategories(d?.categories || []))
      .catch(() => {});
  }, [sourceId]);

  // Set SEO title once source is loaded
  useEffect(() => {
    if (source) {
      document.title = `${source.name} News Highlights — Daily News Highlights`;
    }
  }, [source]);

  // Build API params
  const buildParams = useCallback((p) => {
    const params = { page: p, limit: PAGE_SIZE, source: sourceId };
    if (activeCategory) params.category = activeCategory;
    return params;
  }, [sourceId, activeCategory]);

  // Fetch stories
  useEffect(() => {
    const ver = ++reqVer.current;
    setLoading(true);
    setError(false);
    setPage(1);
    snapshot.current = null;
    getArchive(buildParams(1))
      .then(d => {
        if (ver !== reqVer.current) return;
        setStories(d?.stories || []);
        snapshot.current = d.as_of;
        setHasMore(1 < (d?.pages || 1));
        setLoading(false);
      })
      .catch(() => { if (ver === reqVer.current) { setError(true); setLoading(false); } });
    return () => { reqVer.current = ver + 1; };
  }, [buildParams]);

  const loadMore = () => {
    if (loadingMore) return;
    const ver = reqVer.current;
    const next = page + 1;
    setLoadingMore(true);
    getArchive({ ...buildParams(next), as_of: snapshot.current })
      .then(d => {
        if (ver !== reqVer.current) return;
        setStories(prev => [
          ...new Map([...prev, ...(d?.stories || [])].map(s => [s.id, s])).values()
        ]);
        setPage(next);
        setHasMore(next < (d?.pages || 1));
        setLoadingMore(false);
      })
      .catch(() => { if (ver === reqVer.current) { setError(true); setLoadingMore(false); } });
  };

  const headerUser = user ? {
    name: user.name || user.email,
    initials: (user.name || user.email || "U").slice(0, 2).toUpperCase(),
    email: user.email,
    avatar_url: user.avatar_url || null,
    subscription_tier: user.subscription_tier,
    is_admin: user.is_admin,
    plan: user.subscription_tier,
  } : null;

  if (srcError) {
    return (
      <div className="min-h-screen bg-[var(--paper)] text-[var(--ink)]">
        <SiteHeader activeNav="Sources" user={headerUser} />
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
          <p className="font-serif text-[22px] text-[var(--ink)] mb-2">Source not found</p>
          <p className="font-mono text-[12px] text-[var(--muted)] mb-6">
            "{sourceId}" doesn't match any source in the network.
          </p>
          <Link to="/sources" className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.1em] text-[var(--crimson)] hover:underline">
            <ArrowLeft size={12} /> Back to Sources
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--paper)] text-[var(--ink)]">
      <SiteHeader activeNav="Sources" user={headerUser} />

      {/* Hero ── source identity */}
      <section className="border-b border-[var(--hairline)] py-8 sm:py-12" data-testid="source-detail-hero">
        <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10">

          {/* Breadcrumb */}
          <nav className="flex items-center gap-1.5 mb-5 font-mono text-[10.5px] text-[var(--muted)]" aria-label="breadcrumb">
            <Link to="/sources" className="hover:text-[var(--crimson)] transition-colors">Sources</Link>
            <ChevronRight size={11} />
            <span className="text-[var(--ink-2)]">{source?.name || sourceId}</span>
          </nav>

          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div>
              {/* Badges */}
              <div className="flex items-center gap-2 flex-wrap mb-3">
                <RssBadge />
                {source && <TypeBadge type={source.source_type} />}
                {source?.category_label && (
                  <span className="inline-flex items-center h-5 px-2.5 rounded-[4px] border border-[var(--hairline)] bg-[var(--surface-2)] font-mono text-[9px] uppercase tracking-[0.06em] text-[var(--muted)]">
                    {source.category_label}
                  </span>
                )}
              </div>

              {/* Name */}
              <h1
                className="font-serif text-[32px] sm:text-[44px] lg:text-[52px] leading-[1.05] tracking-[-0.01em] text-[var(--ink)]"
                data-testid="source-detail-name"
              >
                {source?.name || <span className="opacity-30">Loading…</span>}
              </h1>

              {/* Meta row */}
              <div className="flex items-center gap-4 flex-wrap mt-3">
                {source?.country_name && (
                  <span className="font-mono text-[11px] text-[var(--muted)]">{source.country_name}</span>
                )}
                {source?.articles_today > 0 && (
                  <span className="font-mono text-[11px] text-[var(--muted)]">
                    {source.articles_today} articles today
                  </span>
                )}
                {source?.story_count > 0 && (
                  <span className="inline-flex items-center gap-1 font-mono text-[11px] text-[var(--ink-2)]">
                    <BookOpen size={11} />
                    {source.story_count.toLocaleString()} stories on DNH
                  </span>
                )}
                {source?.credibility_score > 0 && (
                  <span className="font-mono text-[11px] text-[var(--muted)]">
                    Credibility {source.credibility_score}/100
                  </span>
                )}
                {source?.uptime_7d > 0 && (
                  <span className="font-mono text-[11px] text-[var(--muted)]">
                    {source.uptime_7d}% uptime
                  </span>
                )}
              </div>
            </div>

            {/* External link */}
            {source?.web_url && (
              <a
                href={source.web_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-[var(--hairline)] font-mono text-[10.5px] uppercase tracking-[0.08em] text-[var(--muted)] hover:text-[var(--crimson)] hover:border-[var(--crimson)] transition-colors shrink-0 mt-1"
                data-testid="source-detail-external-link"
              >
                <ExternalLink size={12} /> Visit {source.name}
              </a>
            )}
          </div>

          {/* Category filter */}
          {categories.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap mt-6" data-testid="source-detail-category-filters">
              <CatPill label="All" active={!activeCategory} onClick={() => setActiveCategory("")} />
              {categories.map(c => (
                <CatPill
                  key={c.slug}
                  label={c.label || c.slug}
                  active={activeCategory === c.slug}
                  onClick={() => setActiveCategory(c.slug)}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Stories grid */}
      <main className="py-10" data-testid="source-detail-main">
        <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10">
          {error && (
            <p role="alert" className="mb-4 text-sm text-[var(--crimson)]">
              Couldn't load stories. Please try again.
            </p>
          )}

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6" data-testid="source-detail-loading">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="animate-pulse rounded-[14px] border border-[var(--hairline)] h-[300px] bg-[var(--surface-2)]" />
              ))}
            </div>
          ) : stories.length === 0 ? (
            <div className="text-center py-20" data-testid="source-detail-empty">
              <p className="font-mono text-[11.5px] tracking-[0.1em] uppercase text-[var(--muted)]">
                No highlights from this source yet
                {activeCategory ? ` in ${activeCategory}` : ""}.
              </p>
              {activeCategory && (
                <button
                  onClick={() => setActiveCategory("")}
                  className="mt-4 font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--crimson)] hover:underline"
                >
                  Clear filter
                </button>
              )}
            </div>
          ) : (
            <motion.div
              layout
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6"
              data-testid="source-detail-grid"
            >
              {(() => {
                const seenUrls = new Set();
                return stories.map(s => {
                  const url = s.image_url || "";
                  const alreadySeen = url ? seenUrls.has(url) : false;
                  if (url) seenUrls.add(url);
                  return (
                    <HighlightCard
                      key={s.id}
                      story={s}
                      dimmed={!!activeStory && activeStory.id !== s.id}
                      onOpen={setActiveStory}
                      imageAlreadySeen={alreadySeen}
                    />
                  );
                });
              })()}
            </motion.div>
          )}

          {/* Load more */}
          {!loading && hasMore && (
            <div className="mt-8 flex justify-center">
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                data-testid="source-detail-load-more"
                className="inline-flex items-center gap-1.5 h-11 px-6 rounded-full border border-[var(--hairline)] font-mono text-[11px] tracking-[0.1em] uppercase text-[var(--ink-2)] hover:border-[var(--ink)] transition-colors disabled:opacity-50"
              >
                <RefreshCw size={12} className={loadingMore ? "animate-spin" : ""} />
                {loadingMore ? "Loading…" : "Load more stories"}
              </button>
            </div>
          )}

          {/* Back link */}
          {!loading && (
            <div className="mt-12 pt-8 border-t border-[var(--hairline)] text-center">
              <Link
                to="/sources"
                className="inline-flex items-center gap-1.5 font-mono text-[11px] tracking-[0.12em] uppercase text-[var(--ink-2)] hover:text-[var(--crimson)]"
                data-testid="source-detail-back-link"
              >
                <ArrowLeft size={13} /> Back to all sources
              </Link>
            </div>
          )}
        </div>

        {/* ── Related Sources ────────────────────────────────── */}
        {related.length > 0 && (
          <section className="mt-14 border-t border-[var(--hairline)] pt-10" data-testid="related-sources-section">
            <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--crimson)] mb-0.5">
                    More from {source?.country_name}
                  </p>
                  <h2 className="font-serif text-[18px] text-[var(--ink)]">
                    Other sources in this country
                  </h2>
                </div>
                <Link
                  to={`/sources?country=${source?.country_slug}`}
                  className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-[var(--muted)] hover:text-[var(--crimson)] transition-colors hidden sm:block"
                >
                  View all →
                </Link>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="related-sources-grid">
                {related.map(rel => (
                  <Link
                    key={rel.source_id}
                    to={`/sources/${encodeURIComponent(rel.source_id)}`}
                    className="group flex flex-col gap-2 p-4 border border-[var(--hairline)] rounded-[8px] hover:border-[var(--ink)] hover:shadow-sm transition-all"
                    data-testid={`related-source-${rel.source_id}`}
                  >
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className={`inline-flex items-center gap-1 h-5 px-2 rounded-[4px] border font-mono text-[9px] uppercase tracking-[0.06em] ${
                        rel.source_type === "official"
                          ? "bg-amber-50 text-amber-700 border-amber-200"
                          : "bg-emerald-50 text-emerald-700 border-emerald-200"
                      }`}>
                        {rel.source_type === "official" ? <Shield size={9} /> : <Newspaper size={9} />}
                        {rel.category_label}
                      </span>
                    </div>
                    <p className="font-serif text-[13.5px] leading-snug text-[var(--ink)] group-hover:text-[var(--crimson)] transition-colors line-clamp-2">
                      {rel.name}
                    </p>
                    <div className="flex items-center gap-2 flex-wrap mt-auto pt-1">
                      {rel.articles_today > 0 && (
                        <span className="font-mono text-[10px] text-[var(--muted)]">{rel.articles_today} today</span>
                      )}
                      {rel.story_count > 0 && (
                        <span className="font-mono text-[10px] text-[var(--ink-2)]">{rel.story_count.toLocaleString()} stories</span>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <AnimatePresence>
        {activeStory && (
          <HighlightBriefPanel story={activeStory} onClose={() => setActiveStory(null)} />
        )}
      </AnimatePresence>

      <SiteFooter variant="global" />
    </div>
  );
}
